import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Mail, Plus, Upload, Download, UserX, UserCheck, AlertCircle, Loader2, Trash2, XCircle } from 'lucide-react';

/**
 * Teachers tab — invite, bulk-upload (xlsx), list, soft delete / reactivate.
 */
export default function TeachersTab({ school, subjects, brandColor }) {
  const [teachers, setTeachers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject_id: '' });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);  // teacher object
  const [deletingId, setDeletingId] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);  // pending-invite object
  const [revokingId, setRevokingId] = useState(null);

  const schoolId = school?.school_id;

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const [t, i] = await Promise.all([
        api.get(`/schools/${schoolId}/teachers`),
        api.get(`/schools/${schoolId}/teachers/invites`),
      ]);
      setTeachers(t.data || []);
      setInvites(i.data || []);
    } catch {
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  const subjectById = (id) => subjects.find((s) => s.subject_id === id);

  const submitInvite = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.subject_id) {
      toast.error('Full Name, Email, and Subject are required'); return;
    }
    setSaving(true);
    try {
      await api.post(`/schools/${schoolId}/teachers/invites`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        subject_id: form.subject_id,
      });
      toast.success(`Invite sent to ${form.email.trim()}`);
      setInviteOpen(false);
      setForm({ name: '', email: '', subject_id: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to invite teacher');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (teacher) => {
    const target = teacher.status === 'inactive' ? 'active' : 'inactive';
    try {
      await api.patch(`/schools/${schoolId}/teachers/${teacher.user_id}`, { status: target });
      toast.success(`${teacher.name} ${target === 'inactive' ? 'deactivated' : 'reactivated'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    }
  };

  // Hard delete (School Lifecycle Slice 2)
  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.user_id);
    try {
      await api.delete(`/school-admin/users/${confirmDelete.user_id}`);
      toast.success(`${confirmDelete.name} permanently removed from your school.`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete teacher');
    } finally {
      setDeletingId(null);
    }
  };

  // Revoke pending invite
  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    setRevokingId(confirmRevoke.invite_id);
    try {
      await api.post(`/invites/${confirmRevoke.invite_id}/revoke`);
      toast.success(`Invite for ${confirmRevoke.email} revoked.`);
      setConfirmRevoke(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke invite');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div data-testid="teachers-tab">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Teachers</h2>
          <p className="text-sm text-slate-600">
            Invite individual teachers or upload an Excel file with multiple teachers.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-invite-btn"
          >
            <Upload size={14} className="mr-1.5" /> Bulk Invite (xlsx)
          </Button>
          <Button
            onClick={() => setInviteOpen(true)}
            style={{ backgroundColor: brandColor }}
            className="text-white"
            data-testid="invite-teacher-btn"
          >
            <Plus size={14} className="mr-1.5" /> Invite Teacher
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500">Loading…</div>
      ) : (
        <>
          {invites.length > 0 && (
            <section className="mb-6" data-testid="pending-invites-section">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                Pending invites ({invites.length})
              </h3>
              <div className="grid gap-2">
                {invites.map((inv) => (
                  <div
                    key={inv.invite_id}
                    className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3"
                    data-testid={`pending-invite-${inv.email}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Mail size={16} className="text-amber-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 text-sm truncate">
                          {inv.name || inv.email}
                        </div>
                        <div className="text-xs text-slate-600 truncate">
                          {inv.email}
                          {inv.subject_id && subjectById(inv.subject_id) && (
                            <span> · {subjectById(inv.subject_id).name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-amber-700 hidden sm:inline">Awaiting acceptance</span>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setConfirmRevoke(inv)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`teacher-invite-revoke-${inv.invite_id}`}
                        title="Revoke this pending invite"
                      >
                        <XCircle size={14} className="mr-1" /> Revoke
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            Active teachers ({teachers.filter((t) => t.status !== 'inactive').length})
          </h3>
          {teachers.length === 0 ? (
            <div
              className="border-2 border-dashed rounded-xl py-16 text-center bg-white/60"
              data-testid="teachers-empty"
            >
              <p className="text-slate-600 font-medium">No teachers yet</p>
              <p className="text-sm text-slate-500 mt-1">
                Invite your first teacher using the button above.
              </p>
            </div>
          ) : (
            <div className="grid gap-2" data-testid="teachers-list">
              {teachers.map((t) => (
                <div
                  key={t.user_id}
                  className={`bg-white border rounded-lg px-4 py-3 flex items-center justify-between ${
                    t.status === 'inactive' ? 'opacity-60' : ''
                  }`}
                  data-testid={`teacher-card-${t.user_id}`}
                >
                  <div>
                    <div className="font-medium text-slate-900 text-sm">
                      {t.name}
                      {t.status === 'inactive' && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{t.email}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => toggleStatus(t)}
                      data-testid={`teacher-toggle-${t.user_id}`}
                    >
                      {t.status === 'inactive' ? (
                        <><UserCheck size={14} className="mr-1" /> Reactivate</>
                      ) : (
                        <><UserX size={14} className="mr-1" /> Deactivate</>
                      )}
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setConfirmDelete(t)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`teacher-delete-${t.user_id}`}
                      title="Permanently delete this teacher"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Individual invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-teacher-dialog">
          <DialogHeader>
            <DialogTitle>Invite a Teacher</DialogTitle>
            <DialogDescription>
              We'll email them a link to accept. They sign in with a 6-digit code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="teacher-name">Full Name *</Label>
              <Input
                id="teacher-name"
                data-testid="teacher-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Asha Kumar"
              />
            </div>
            <div>
              <Label htmlFor="teacher-email">Email Address *</Label>
              <Input
                id="teacher-email" type="email"
                data-testid="teacher-email-input"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="asha@school.edu"
              />
            </div>
            <div>
              <Label>Subject *</Label>
              <Select
                value={form.subject_id}
                onValueChange={(v) => setForm((f) => ({ ...f, subject_id: v }))}
              >
                <SelectTrigger data-testid="teacher-subject-select">
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem
                      key={s.subject_id} value={s.subject_id}
                      data-testid={`teacher-subject-opt-${s.short_code}`}
                    >
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline" onClick={() => setInviteOpen(false)}
              disabled={saving}
              data-testid="invite-teacher-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitInvite} disabled={saving}
              style={{ backgroundColor: brandColor }}
              className="text-white"
              data-testid="invite-teacher-submit"
            >
              {saving ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk upload dialog */}
      <BulkUploadDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        schoolId={schoolId}
        brandColor={brandColor}
        onDone={() => { setBulkOpen(false); load(); }}
      />

      {/* Hard delete confirmation (School Lifecycle Slice 2) */}
      <Dialog open={confirmDelete !== null} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <DialogContent data-testid="teacher-delete-dialog">
          {confirmDelete && (
            <>
              <DialogHeader>
                <DialogTitle className="text-red-700">
                  Delete {confirmDelete.name}?
                </DialogTitle>
                <DialogDescription>
                  This permanently removes {confirmDelete.email || confirmDelete.name} from your
                  school, along with their teaching assignments. <b>This cannot be undone.</b>
                  <br /><br />
                  If you only want to pause their access for now, use <b>Deactivate</b> instead —
                  that keeps their record and lets you reactivate later.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(null)}
                  disabled={deletingId === confirmDelete.user_id}
                  data-testid="teacher-delete-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={deletingId === confirmDelete.user_id}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="teacher-delete-confirm"
                >
                  {deletingId === confirmDelete.user_id ? 'Deleting…' : 'Delete forever'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Revoke pending invite confirmation */}
      <Dialog open={confirmRevoke !== null} onOpenChange={(v) => { if (!v) setConfirmRevoke(null); }}>
        <DialogContent data-testid="teacher-invite-revoke-dialog">
          {confirmRevoke && (
            <>
              <DialogHeader>
                <DialogTitle>Revoke invite for {confirmRevoke.name || confirmRevoke.email}?</DialogTitle>
                <DialogDescription>
                  The link emailed to <b>{confirmRevoke.email}</b> will stop working.
                  They won't be able to accept this invite anymore. You can re-invite them later if needed.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmRevoke(null)}
                  disabled={revokingId === confirmRevoke.invite_id}
                  data-testid="teacher-invite-revoke-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRevoke}
                  disabled={revokingId === confirmRevoke.invite_id}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="teacher-invite-revoke-confirm"
                >
                  {revokingId === confirmRevoke.invite_id ? 'Revoking…' : 'Revoke invite'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Bulk xlsx upload dialog — strict validation.
 */
function BulkUploadDialog({ open, onOpenChange, schoolId, brandColor, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) { setFile(null); setErrors(null); setResult(null); }
  }, [open]);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/schools/${schoolId}/teachers/template.xlsx`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'yukti-teachers-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setErrors(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(
        `/schools/${schoolId}/teachers/invites/bulk`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(data);
      toast.success(`Invited ${data.invited} teachers`);
      setTimeout(() => onDone(), 1200);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        setErrors(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="bulk-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle>Bulk Invite Teachers</DialogTitle>
          <DialogDescription>
            Download the template, fill in up to 200 teachers, then upload.
            If any row has an error, the whole file is rejected — fix and retry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Button
            variant="outline" onClick={downloadTemplate}
            className="w-full"
            data-testid="download-template-btn"
          >
            <Download size={14} className="mr-1.5" /> Download Excel template
          </Button>

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setErrors(null); setResult(null);
            }}
            data-testid="bulk-upload-file-input"
          />
          {!file ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full border-2 border-dashed border-slate-200 rounded-lg py-10 flex flex-col items-center gap-2 hover:border-indigo-300 hover:bg-indigo-50/40"
              data-testid="bulk-upload-trigger"
            >
              <Upload size={20} className="text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Click to select .xlsx file</span>
              <span className="text-xs text-slate-400">Use the template above as a guide</span>
            </button>
          ) : (
            <div className="border rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">{file.name}</div>
                <div className="text-xs text-slate-500">
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <Button
                variant="ghost" size="sm"
                onClick={() => inputRef.current?.click()}
              >
                Change
              </Button>
            </div>
          )}

          {errors && (
            <div
              className="border border-red-200 bg-red-50 rounded-lg p-4"
              data-testid="bulk-upload-errors"
            >
              <div className="flex items-start gap-2 mb-2">
                <AlertCircle size={16} className="text-red-600 mt-0.5" />
                <div className="font-medium text-red-900 text-sm">{errors.message}</div>
              </div>
              <ul className="text-xs text-red-800 space-y-1 max-h-48 overflow-y-auto pl-6">
                {errors.errors.map((e, i) => (
                  <li key={e.row ?? i}>
                    <span className="font-mono font-semibold">Row {e.row}</span>
                    {e.name || e.email ? ` (${e.name || e.email})` : ''}:
                    {' '}{e.errors.join('; ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result && (
            <div
              className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 text-sm"
              data-testid="bulk-upload-success"
            >
              Invited {result.invited} teacher(s). Closing…
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
          >
            Close
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            style={{ backgroundColor: brandColor }}
            className="text-white"
            data-testid="bulk-upload-submit"
          >
            {uploading
              ? <><Loader2 size={14} className="mr-1 animate-spin" /> Uploading…</>
              : 'Upload and send invites'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
